import React, { useState } from 'react';
import { useStyles } from './DownloadCards.style';
import DownloadCardsIcon from '../../assets/icons/downloadCards.svg';
import { DownloadType } from '../../pages//Assessment/Assessment.types';
import DownloadLimitModal from '../Assessment/Modals/DownloadLimitModal.component';
import { DownloadExamTaskGroupSolution, ExamTaskGroupSolutionDownloadResult } from '../../models/assessment.types';
import { useParams } from 'react-router-dom';
import { AssessmentSessionProcessTypeId } from '../../services/exams/assessmentSession/assessments.types';
import { SnackbarDownload } from '../../components/SnackbarDownload/SnackbarDownload.component';
import { ButtonOne } from '../../components/ButtonOne/ButtonOne.component';
import { getProcessLabel } from '../../utils/assessmentSessionProcess';
import { UnreadMessagePrompt } from '../../components/Prompt/UnreadMessage/UnreadMessagePrompt.component';
import { ConversationType, Priority } from '../../models/communication.types';
import { hasUnreadMessages } from '../../services/communication/communication.service';
import CardsDownloaderContainer from '../../components/CardsDownloaderContainer.component';

interface DownloadCardsProps {
  onDownload: () => void;
  processTypeId: AssessmentSessionProcessTypeId;
  disabled: boolean;
  pageSize: number;
  sheetCodeWithTaskGroupName: string;
  onUnreadMessagesConfirm: (url: string) => void;
}

export const DownloadCards: React.FC<DownloadCardsProps> = (props) => {
  const classes = useStyles();
  const { assessmentSessionId } = useParams() as {
    assessmentSessionId: string;
    processTypeId: string;
  };

  const [openDownloadLimitModal, setOpenDownloadLimitModal] = useState<boolean>(false);
  const [isCardLimit, setIsCardLimit] = useState<boolean>(false);
  const [noMoreCards, setNoMoreCards] = useState<boolean>(false);
  const [isSnackbarVisible, setIsSnackbarVisible] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [unreadMessagePrompt, setUnreadMessagePrompt] = useState<{
    isOpen: boolean;
    unreadConversationType: ConversationType;
  }>({ isOpen: false, unreadConversationType: ConversationType.Standard });

  const handleCloseLimitModal = () => {
    setOpenDownloadLimitModal(false);
    setIsCardLimit(false);
    setNoMoreCards(false);
  };

  const handleDownloadFinish = async (downloadType: DownloadType, response: DownloadExamTaskGroupSolution) => {
    switch (response.solutionDownloadResult) {
      case ExamTaskGroupSolutionDownloadResult.NoneExceededLimits:
        setOpenDownloadLimitModal(true);
        setIsCardLimit(true);
        break;
      case ExamTaskGroupSolutionDownloadResult.NoneNoMoreSolutions:
        setOpenDownloadLimitModal(true);
        setNoMoreCards(true);
        break;
      case ExamTaskGroupSolutionDownloadResult.NoneProcessFinished:
      case ExamTaskGroupSolutionDownloadResult.NoneExaminerBlocked:
      case ExamTaskGroupSolutionDownloadResult.NoneInvalidProcess:
        break;
      case ExamTaskGroupSolutionDownloadResult.FilledUpToLimits:
      case ExamTaskGroupSolutionDownloadResult.SingleSolution:
        await new Promise((r) => setTimeout(r, 2000));
        setIsSnackbarVisible(true);
        setSnackbarMessage(
          downloadType === DownloadType.fillToLimits ? `Pobrano ${response.solutions.length} kart` : 'Karta pobrana',
        );
        props.onDownload();
        break;
      case ExamTaskGroupSolutionDownloadResult.ExaminerShouldReadMessages:
        hasUnreadMessages(Priority.High)
          .then((result) => {
            if (result.status === 200) {
              setUnreadMessagePrompt({
                isOpen: true,
                unreadConversationType: result.data,
              });
            }
          })
          .catch((error) => console.error(error));
        break;
      default:
        break;
    }
  };

  return (
    <React.Fragment>
      <SnackbarDownload
        open={isSnackbarVisible}
        onClose={() => setIsSnackbarVisible(false)}
        autoHideDuration={4000}
        text={snackbarMessage}
      />
      <div className={classes.grayContainer}>
        <img src={DownloadCardsIcon} alt="ikona pobierania kart" />
        <span className={classes.subContainer}>
          <div className={classes.text}>
            W tej chwili nie posiadasz żadnych kart. Pobierz karty by móc rozpocząć{' '}
            {getProcessLabel(props.processTypeId).label.toLowerCase()}.
          </div>
          <CardsDownloaderContainer
            processTypeId={props.processTypeId}
            pageSize={props.pageSize}
            assessmentSessionId={assessmentSessionId}
            onDownloadFinish={handleDownloadFinish}
            onRender={(onDownloadStart) => {
              return (
                <div className={classes.buttonsContainer}>
                  <ButtonOne
                    className={classes.singleButton}
                    disabled={props.disabled}
                    onClick={() => onDownloadStart(DownloadType.single)}
                  >
                    pobierz jedną kartę
                  </ButtonOne>
                  <ButtonOne
                    className={classes.groupButton}
                    disabled={props.disabled}
                    onClick={() => onDownloadStart(DownloadType.fillToLimits)}
                  >
                    pobierz wszystkie do limitu
                  </ButtonOne>
                </div>
              );
            }}
          ></CardsDownloaderContainer>
        </span>
      </div>
      {openDownloadLimitModal && (
        <DownloadLimitModal
          open={openDownloadLimitModal}
          noMoreCards={noMoreCards}
          isCardLimit={isCardLimit}
          handleClose={handleCloseLimitModal}
          processTypeId={props.processTypeId}
        />
      )}
      {unreadMessagePrompt.isOpen && (
        <UnreadMessagePrompt
          action={'download'}
          unreadConversationType={unreadMessagePrompt.unreadConversationType}
          sheetCodeWithTaskGroupName={props.sheetCodeWithTaskGroupName}
          onConfirm={props.onUnreadMessagesConfirm}
        />
      )}
    </React.Fragment>
  );
};
